import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { submissionService } from '../../services/submissionService';



import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import StatsCard from './components/StatsCard';
import FilterPanel from './components/FilterPanel';
import SubmissionTable from './components/SubmissionTable';
import BulkActionsBar from './components/BulkActionsBar';


import QuickStats from './components/QuickStats';

export default function AdminDashboard() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    company: '',
    status: '',
    category: '',
    dateFrom: '',
    dateTo: '',
    department: '',
    completionRate: ''
  });

  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [viewMode, setViewMode] = useState('table');

  useEffect(() => {
    // Load submissions in background without blocking UI
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      setError('');
      const data = await submissionService?.getAll();
      setSubmissions(data || []);
    } catch (err) {
      console.error('Error loading submissions:', err);
      setError(err?.message || 'Failed to load submissions');
      setSubmissions([]); // Set empty array on error so UI can still render
    }
  };

  const handleStatusUpdate = async (submissionId, newStatus) => {
    try {
      await submissionService?.updateStatus(submissionId, newStatus);
      await loadSubmissions(); // Reload data
    } catch (err) {
      setError(err?.message);
    }
  };

  const companies = [
  { value: 'Eppme Digital Technologies Ltd', label: 'Eppme Digital Technologies Ltd' },
  { value: 'Zetavista Analytics', label: 'Zetavista Analytics' },
  { value: 'DeGrey Digital Technologies Ltd', label: 'DeGrey Digital Technologies Ltd' }];


  const statuses = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'in-review', label: 'In Review' }];


  const categories = [
  { value: 'new-hire', label: 'New Hire' },
  { value: 'nysc-corps', label: 'NYSC Corps Member' },
  { value: 'intern', label: 'Intern' },
  { value: 'contract', label: 'Contract' }];


  const quickStats = [
  {
    label: 'Total Submissions',
    value: '247',
    icon: 'Users',
    color: 'var(--color-primary)',
    subtext: 'All time submissions'
  },
  {
    label: 'Pending Review',
    value: '42',
    icon: 'Clock',
    color: 'var(--color-warning)',
    subtext: 'Awaiting action'
  },
  {
    label: 'Approved Today',
    value: '18',
    icon: 'CheckCircle',
    color: 'var(--color-success)',
    subtext: '+12% from yesterday'
  },
  {
    label: 'Avg. Completion',
    value: '87%',
    icon: 'TrendingUp',
    color: 'var(--color-accent)',
    subtext: 'Across all forms'
  }];


  const filteredSubmissions = useMemo(() => {
    return submissions?.filter((submission) => {
      const fullName = submission?.fullName || submission?.name || '';
      const email = submission?.email || '';
      const companyName = submission?.company?.name || submission?.company || '';
      const status = submission?.submissionStatus || submission?.status || '';
      
      if (filters?.search && !fullName?.toLowerCase()?.includes(filters?.search?.toLowerCase()) &&
      !email?.toLowerCase()?.includes(filters?.search?.toLowerCase())) {
        return false;
      }
      if (filters?.company && companyName !== filters?.company) return false;
      if (filters?.status && status !== filters?.status) return false;
      if (filters?.department && submission?.department !== filters?.department) return false;
      if (filters?.completionRate) {
        const [min, max] = filters?.completionRate?.split('-')?.map(Number);
        const completionRate = submission?.completionRate || 0;
        if (completionRate < min || completionRate > max) return false;
      }
      return true;
    });
  }, [filters, submissions]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      company: '',
      status: '',
      category: '',
      dateFrom: '',
      dateTo: '',
      department: '',
      completionRate: ''
    });
  };

  const handleSelectAll = () => {
    if (selectedIds?.length === filteredSubmissions?.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredSubmissions?.map((s) => s?.id));
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
    prev?.includes(id) ? prev?.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleApprove = (id) => {
    console.log('Approving submission:', id);
    setSelectedIds((prev) => prev?.filter((i) => i !== id));
  };

  const handleReject = (id) => {
    console.log('Rejecting submission:', id);
    setSelectedIds((prev) => prev?.filter((i) => i !== id));
  };

  const handleBulkApprove = () => {
    console.log('Bulk approving:', selectedIds);
    setSelectedIds([]);
  };

  const handleBulkReject = () => {
    console.log('Bulk rejecting:', selectedIds);
    setSelectedIds([]);
  };

  const handleBulkExport = () => {
    setShowExportModal(true);
  };

  const handleBulkAssign = (assignee) => {
    console.log('Bulk assigning to:', assignee, selectedIds);
    setSelectedIds([]);
  };

  const handleExport = (config) => {
    console.log('Exporting with config:', config);
  };

  const handleSavePreset = () => {
    console.log('Saving filter preset:', filters);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Background Pattern - Enhanced visibility */}
      <div className="fixed inset-0 bg-pattern-dots opacity-20 pointer-events-none"></div>
      {/* Gradient - More pronounced */}
      <div className="fixed inset-0 gradient-professional opacity-10 pointer-events-none"></div>
      
      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard
            title="Total Submissions"
            value="247"
            change="+12.5%"
            changeType="increase"
            icon="Users"
            iconBg="bg-primary/10"
            iconColor="var(--color-primary)" />

          <StatsCard
            title="Pending Review"
            value="42"
            change="-8.2%"
            changeType="decrease"
            icon="Clock"
            iconBg="bg-warning/10"
            iconColor="var(--color-warning)" />

          <StatsCard
            title="Approved Today"
            value="18"
            change="+15.3%"
            changeType="increase"
            icon="CheckCircle"
            iconBg="bg-success/10"
            iconColor="var(--color-success)" />

          <StatsCard
            title="Avg. Completion"
            value="87%"
            change="+3.1%"
            changeType="increase"
            icon="TrendingUp"
            iconBg="bg-accent/10"
            iconColor="var(--color-accent)" />

        </div>

        {/* Quick Stats */}
        <QuickStats stats={quickStats} />

        {/* Navigation Tabs */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2 bg-card rounded-lg p-1 shadow-brand">
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              iconName="Table"
              onClick={() => setViewMode('table')}>

              Table View
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              iconName="Grid"
              onClick={() => setViewMode('grid')}>

              Grid View
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="BarChart3"
              onClick={() => navigate('/analytics-overview')}>

              Analytics
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Settings">

              Settings
            </Button>
          </div>
        </div>

        {/* Filter Panel */}
        <FilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          onSavePreset={handleSavePreset}
          companies={companies}
          statuses={statuses}
          categories={categories} />


        {/* Bulk Actions Bar */}
        <BulkActionsBar
          selectedCount={selectedIds?.length}
          onBulkApprove={handleBulkApprove}
          onBulkReject={handleBulkReject}
          onBulkExport={handleBulkExport}
          onBulkAssign={handleBulkAssign} />


        {/* Error Display */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">
              <strong>Error:</strong> {error}
            </p>
            <button
              onClick={loadSubmissions}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* Results Summary */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-muted-foreground">
            Showing {filteredSubmissions?.length || 0} of {submissions?.length || 0} submissions
          </p>
          <Select
            placeholder="Sort by..."
            options={[
            { value: 'date-desc', label: 'Newest First' },
            { value: 'date-asc', label: 'Oldest First' },
            { value: 'name-asc', label: 'Name (A-Z)' },
            { value: 'completion-desc', label: 'Completion Rate' }]
            }
            className="w-48" />

        </div>

        {/* Submissions Table */}
        {filteredSubmissions?.length > 0 ? (
          <SubmissionTable
            submissions={filteredSubmissions}
            selectedIds={selectedIds}
            onSelectAll={handleSelectAll}
            onSelectOne={handleSelectOne}
            onViewDetails={setSelectedSubmission}
            onApprove={handleApprove}
            onReject={handleReject} />
        ) : (
          <div className="bg-card rounded-lg shadow-brand p-12 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No submissions found</h3>
            <p className="text-sm text-gray-500">
              There are no employee submissions to display yet.
            </p>
          </div>
        )}


        {/* Pagination */}
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-muted-foreground">
            Page 1 of 5
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" iconName="ChevronLeft" disabled>
              Previous
            </Button>
            <Button variant="outline" size="sm">1</Button>
            <Button variant="ghost" size="sm">2</Button>
            <Button variant="ghost" size="sm">3</Button>
            <Button variant="outline" size="sm" iconName="ChevronRight">
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}